import React, { useMemo, useState } from 'react';
import { HbrDomTable, HbrPagination } from '@harbor/elements-react-standalone';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useNavigate } from 'react-router-dom';

import { useLeaderCourseCompletionsByUser } from '../../../hooks/useMoodleLeaderWorkshops';
import { moodleFetch } from '../../../hooks/useMoodleHooks';
import { buildCourseDetailPath } from '../../../support/moodleDetailRoutes';

const PAGE_SIZE_OPTIONS = [30, 50, 100];
const LEADER_RETURN_TO = '/leader';
const ALLOWED_CATEGORIES = new Set(['active workshops', 'train the trainer']);

type CategoryFilter = 'all' | 'active workshops' | 'train the trainer';
type StatusFilter = 'all' | 'completed' | 'in_progress' | 'not_started';
type PillTone = 'success' | 'info' | 'neutral' | 'warning';

type Row = {
  id: string;
  course_id: number;
  course_fullname?: string;
  category_name?: string;
  completion_status?: string;
  completion_percent: number;
  is_ttt: boolean;
  has_certificate: boolean;
};

function safeId(courseId: unknown) {
  return `c_${String(courseId).replace(/[^a-z0-9_-]/gi, '_')}`;
}

function normalizePercent(value: unknown, status: string): number {
  const n = Number(value);
  if (Number.isFinite(n)) {
    return Math.max(0, Math.min(100, n));
  }

  const s = status.trim().toLowerCase();
  if (s === 'completed') return 100;
  if (s === 'in progress') return 50;
  return 0;
}

function getProgressState(percent: number): 'completed' | 'in_progress' | 'not_started' {
  if (percent >= 100) return 'completed';
  if (percent > 0) return 'in_progress';
  return 'not_started';
}

function pillStyle(kind: PillTone): React.CSSProperties {
  const map: Record<PillTone, React.CSSProperties> = {
    success: {
      background: 'var(--hbr-color-success-bg, #e8f5e9)',
      border: '1px solid var(--hbr-color-success-border, #a5d6a7)',
      color: 'var(--hbr-color-success-foreground, #1b5e20)',
    },
    info: {
      background: 'var(--hbr-color-info-bg, #e3f2fd)',
      border: '1px solid var(--hbr-color-info-border, #90caf9)',
      color: 'var(--hbr-color-info-foreground, #0d47a1)',
    },
    neutral: {
      background: 'var(--hbr-color-neutral-bg-medium, #f3f4f6)',
      border: '1px solid var(--hbr-color-neutral-border-weak, #d1d5db)',
      color: 'var(--hbr-color-text-primary, #111827)',
    },
    warning: {
      background: 'var(--hbr-color-warning-bg, #fff8e1)',
      border: '1px solid var(--hbr-color-warning-border, #ffe082)',
      color: 'var(--hbr-color-warning-foreground, #8d6e63)',
    },
  };

  return {
    ...map[kind],
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
  };
}

function metricCard(label: string, value: React.ReactNode, sublabel?: React.ReactNode) {
  return (
    <div
      className="hbr-css__container-card"
      style={{
        padding: 12,
        minHeight: 84,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      <div className="hbr-css__text-secondary-xs">{label}</div>
      <div className="hbr-css__text-primary-md-semibold">{value}</div>
      {sublabel ? <div className="hbr-css__text-secondary-xs">{sublabel}</div> : null}
    </div>
  );
}

export default function WorkshopsUserCourseProgressTable({ email }: { email: string }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useLeaderCourseCompletionsByUser(email);

  const { data: tttStatus } = useQuery({
    queryKey: ['tttUserCertStatus', email],
    queryFn: async () => moodleFetch<any>('moodle/moodle/ttt/user-courses-certificate-status/', { email }),
    ...{
        enabled: !!email,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
  });

  const tttByCourseId = useMemo(() => {
    const map = new Map<number, boolean>();
    const list = Array.isArray(tttStatus?.results) ? tttStatus.results : [];
    for (const r of list) {
      map.set(Number(r.course_id), !!r.has_certificate);
    }
    return map;
  }, [tttStatus]);

  const allRows: Row[] = useMemo(() => {
    const arr = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];

    return arr
      .map((r: any) => {
        const category = String(r.category_name ?? '').trim();
        const status = String(
          r.completion_status ?? (r.is_completed ? 'Completed' : 'Not Started')
        ).trim();
        const course_id = Number(r.course_id);
        const is_ttt = category.toLowerCase() === 'train the trainer';
        const completion_percent = normalizePercent(r.completion_percent, status);

        return {
          id: safeId(course_id),
          course_id,
          course_fullname: r.course_fullname ?? r.course_shortname ?? `Course ${course_id}`,
          category_name: category,
          completion_status: status,
          completion_percent,
          is_ttt,
          has_certificate: !!tttByCourseId.get(course_id),
        };
      })
      .filter((r) => Number.isFinite(r.course_id))
      .filter((r) => ALLOWED_CATEGORIES.has(String(r.category_name ?? '').toLowerCase()));
  }, [data, tttByCourseId]);

  const metrics = useMemo(() => {
    const total = allRows.length;
    const completed = allRows.filter((r) => r.completion_percent >= 100).length;
    const inProgress = allRows.filter((r) => r.completion_percent > 0 && r.completion_percent < 100).length;
    const notStarted = allRows.filter((r) => r.completion_percent <= 0).length;
    const ttt = allRows.filter((r) => r.is_ttt).length;
    const qualified = allRows.filter((r) => r.is_ttt && r.has_certificate).length;

    return { total, completed, inProgress, notStarted, ttt, qualified };
  }, [allRows]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filteredRows = useMemo(() => {
    let rows = [...allRows];

    if (categoryFilter !== 'all') {
      rows = rows.filter(
        (r) => String(r.category_name ?? '').toLowerCase() === categoryFilter
      );
    }

    if (statusFilter !== 'all') {
      rows = rows.filter((r) => getProgressState(r.completion_percent) === statusFilter);
    }

    rows.sort((a, b) => {
      if (b.completion_percent !== a.completion_percent) {
        return b.completion_percent - a.completion_percent;
      }

      if (a.is_ttt !== b.is_ttt) {
        return a.is_ttt ? -1 : 1;
      }

      return String(a.course_fullname ?? '').localeCompare(String(b.course_fullname ?? ''));
    });

    return rows;
  }, [allRows, categoryFilter, statusFilter]);

  const paged = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, pageIndex, pageSize]);

  if (isLoading) {
    return <div className="hbr-css__text-secondary-sm">Loading course progress…</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{(error as Error).message}</div>;
  }

  if (!allRows.length) {
    return (
      <div className="hbr-css__text-secondary-sm">
        No Active Workshops / Train the Trainer courses found for {email}.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
      >
        {metricCard('Courses', metrics.total)}
        {metricCard('Completed', metrics.completed)}
        {metricCard('In Progress', metrics.inProgress)}
        {metricCard('Not Started', metrics.notStarted)}
        {metricCard('TTT Courses', metrics.ttt)}
        {metricCard(
          'TTT Qualified',
          metrics.qualified,
          metrics.ttt ? `${Math.round((metrics.qualified / metrics.ttt) * 100)}% qualified` : '—'
        )}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select
          className="border rounded px-2 py-1 text-sm"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value as CategoryFilter);
            setPageIndex(0);
          }}
        >
          <option value="all">All Categories</option>
          <option value="active workshops">Active Workshops</option>
          <option value="train the trainer">Train the Trainer</option>
        </select>

        <select
          className="border rounded px-2 py-1 text-sm"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPageIndex(0);
          }}
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="in_progress">In Progress</option>
          <option value="not_started">Not Started</option>
        </select>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <HbrDomTable
          autoLayout
          style={{ display: 'block', width: '100%' }}
          columns={[
            { id: 'course', name: 'Course', width: '40%' },
            { id: 'category_name', name: 'Category', width: '16%' },
            { id: 'completion_status', name: 'Status', width: '16%' },
            { id: 'completion_percent', name: 'Completion %', width: '12%' },
            { id: 'ttt_proctor', name: 'TTT Readiness', width: '16%' },
          ] as any}
          data={paged as any}
        >
          {paged.flatMap((r) => {
            const progressState = getProgressState(r.completion_percent);

            const statusTone: PillTone =
              progressState === 'completed'
                ? 'success'
                : progressState === 'in_progress'
                  ? 'info'
                  : 'neutral';

            const statusLabel =
              progressState === 'completed'
                ? 'Completed'
                : progressState === 'in_progress'
                  ? 'In Progress'
                  : 'Not Started';

            const courseHref = buildCourseDetailPath(r.course_id, { returnTo: LEADER_RETURN_TO });

            return [
              <div
                key={`n-${r.id}`}
                slot={`row-${r.id}-column-course`}
                className="min-h-10 min-w-0 flex items-center font-semibold truncate"
                title={String(r.course_fullname ?? '')}
              >
                {courseHref ? (
                  <button
                    className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left truncate"
                    type="button"
                    onClick={() => navigate(courseHref)}
                    title={String(r.course_fullname ?? '')}
                  >
                    {r.course_fullname ?? '—'}
                  </button>
                ) : (
                  r.course_fullname ?? '—'
                )}
              </div>,

              <div
                key={`cat-${r.id}`}
                slot={`row-${r.id}-column-category_name`}
                className="min-h-10 flex items-center"
              >
                <span style={pillStyle('neutral')}>{r.category_name ?? '—'}</span>
              </div>,

              <div
                key={`st-${r.id}`}
                slot={`row-${r.id}-column-completion_status`}
                className="min-h-10 flex items-center"
              >
                <span style={pillStyle(statusTone)}>{statusLabel}</span>
              </div>,

              <div
                key={`cp-${r.id}`}
                slot={`row-${r.id}-column-completion_percent`}
                className="min-h-10 flex items-center justify-end font-semibold"
              >
                {r.completion_percent}%
              </div>,

              <div
                key={`tp-${r.id}`}
                slot={`row-${r.id}-column-ttt_proctor`}
                className="min-h-10 flex items-center"
              >
                {r.is_ttt ? (
                  r.has_certificate ? (
                    <span style={pillStyle('success')}>Qualified</span>
                  ) : (
                    <span style={pillStyle('warning')}>Not Yet Qualified</span>
                  )
                ) : (
                  <span className="hbr-css__text-secondary-sm">N/A</span>
                )}
              </div>,
            ];
          })}
        </HbrDomTable>
      </div>

      <HbrPagination
        itemCount={filteredRows.length}
        pageSize={pageSize}
        page={pageIndex}
        advanced
        pageInfo
        sizeChanger
        pageOptions={PAGE_SIZE_OPTIONS}
        onHbr-size-changed={(e) => {
          setPageSize(e.detail.pageSize);
          setPageIndex(0);
        }}
        onHbr-page-changed={(e) => setPageIndex(e.detail)}
      />
    </div>
  );
}